import { PropsWithChildren, createContext, useState } from 'react'
import {
  CageOption,
  ContextProps,
  InitialDataResponse,
  InitialDataSeaWater,
  Location,
  Option,
  State,
} from './types'
import { ControlServicesProvider } from './ControlServices/ControlServicesContext'
import { useStorage } from '../../hooks/useStorage'

export const SeaWaterContext = createContext({} as ContextProps)

export const SeaWaterProvider = ({ children }: PropsWithChildren) => {
  const [initialData, setInitialData] = useState<InitialDataSeaWater>({
    hasData: false,
    centers: [],
    mortalityTypes: [],
    states: [],
    modules: [],
    cages: [],
  })

  const { isFirstRender, removeStorage } = useStorage({
    initialData,
    setDataFromStorage: (data) => setInitialData(data),
    key: 'aqua-data-seaWater',
  })

  const handleInittialData = (data: InitialDataResponse) => {
    isFirstRender.current = false
    setInitialData({
      ...data,
      centers: formatCentersData(data.locations),
      modules: formatModuleData(data.locations),
      cages: formatCagesData(data.locations),
      states: formatStatesData(data.states),
      hasData: true,
    })
  }

  const formatCentersData = (centers: Location[]) =>
    centers.map((item) => ({
      name: item.name,
      id: item.locationId,
    }))

  const formatModuleData = (centers: Location[]): { [key: number]: Option[] } =>
    Object.fromEntries(
      centers.map(({ locationId, modules }) => [
        locationId,
        modules.map((module) => ({ name: module.name, id: module.moduleId })),
      ]),
    )

  const formatCagesData = (centers: Location[]): CageOption =>
    Object.fromEntries(
      centers.flatMap(({ modules }) =>
        modules.map(({ moduleId, cages }) => [
          moduleId,
          cages.map((cage) => ({ ...cage, id: cage.cageId })),
        ]),
      ),
    )

  const formatStatesData = (data: State[]): Option[] => [
    ...data.map((state) => ({
      id: state.stateId,
      name: state.name,
    })),
  ]

  const handleResetData = async () => {
    await removeStorage()
    setInitialData({
      hasData: false,
      centers: [],
      mortalityTypes: [],
      states: [],
      modules: [],
      cages: [],
    })
  }

  return (
    <SeaWaterContext.Provider
      value={{
        ...initialData,
        handleResetData,
        handleInittialData,
      }}
    >
      <ControlServicesProvider>{children}</ControlServicesProvider>
    </SeaWaterContext.Provider>
  )
}
